import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Contract, Profile, ProfileModuleScreen, User} from '../models/user.model';
import {BehaviorSubject, finalize, forkJoin, mergeMap, Observable} from 'rxjs';
import {jwtDecode} from 'jwt-decode';
import {map} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {Router} from "@angular/router";
import * as aes from 'crypto-js/aes';
import * as Utf8 from 'crypto-js/enc-utf8';
import {JwtHelperService} from "@auth0/angular-jwt";
import {Screen} from "../core/guards/RoleEnum.enum";
import {ThemeService} from "./theme.service";
import {LoaderService} from "./loader.service";

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  jwtHelper = new JwtHelperService();
  baseUrl = environment.accountLoginApiUrl;
  private readonly _contractSelected$: BehaviorSubject<Contract | undefined> = new BehaviorSubject<Contract | undefined>(undefined);
  private readonly _changeLoggedPass: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly _changePassSuccess: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  currentUserSource = new BehaviorSubject<any>(null);
  private static readonly KEY: string = "hyhheyh5ytdFJYFG==";

  constructor(private readonly http: HttpClient,
              private readonly router: Router,
              private readonly loader: LoaderService,
              private readonly themeService: ThemeService) {
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    if (user) {
      this.currentUserSource.next(user);
    }
  }

  get encriptedUser(): string {
    let currentUser = this.getCurrentUser();
    currentUser.contracts = this.getCurrentContractAsList();
    currentUser.permissions = currentUser.permissions?.filter(p => p.contractId === this.getCurrentContract()?.id);
    return this.encrypt(currentUser, true)
  }

  encrypt(value: any, shouldParse?: boolean) {
    if (shouldParse) {
      value = JSON.stringify(value);
    }
    const encrypted: any = aes.encrypt(value, AuthService.KEY).toString();
    return encrypted;
  }

  forgotPassword(email: string): Observable<any> {
    let url: string = `${this.baseUrl}auth/resetPassword`;
    url += `?email=${email}`;
    return this.http.get(url);
  }

  login(model: any): Observable<User> {
    return this.http.post<User>(this.baseUrl + 'auth/login', model).pipe(
      mergeMap((response: User) => {
        const user = response;
        if (!user || !user.contracts || user.contracts.length === 0)
          throw new Error('Usuário não possui contrato associado');
        
        return this.getPermissions(user).pipe(
          finalize(() => this.themeService.reloadMenu()),
          map((enrichData: any[]) => {
            const finalUser = ({
              ...user,
              permissions: enrichData
            } as User);
            localStorage.setItem('user', JSON.stringify(finalUser));
            localStorage.setItem('group', JSON.stringify(finalUser.contracts && finalUser.contracts?.length > 0 ? finalUser.contracts[0] : null));
            this.currentUserSource.next(finalUser);
            return finalUser;
          })
        );
      })
    );

  }

  private getPermissions(user: User): Observable<any> {
    this.loader.putMsgLoader('Consultando permissões...');
    let requests: Observable<any>[] = [];
    user.contracts?.forEach(contract => {
      const url = `${this.baseUrl}auth/acl/modules?userId=${user.id}&contractId=${contract.id}`;
      const headers = new HttpHeaders({
        'Authorization': user.accessToken
      });

      requests.push(this.http.get(url, {headers}));
    })
    return forkJoin(requests).pipe(
      map(response => {
        let res: any[] = [];
        response.forEach((item: any) => {
          res.push(...item);
        });
        return res;
      })
    );
  }

  hasRoles(requiredRoles: Screen[], contract?: Contract): boolean {
    const user = this.getCurrentUser();
    if (!user?.permissions) {
      return false;
    }

    const targetContract = contract ?? this.getCurrentContract();
    const screens = user?.permissions
      ?.filter(permission => permission.contractId === targetContract?.id)
      ?.flatMap(item =>
      item?.profiles?.flatMap(profile =>
        profile?.profileModuleScreens?.filter(pms => pms?.module?.id === 2)
          .map(pms => pms?.screen)
      )
    );

    return screens.some(screen =>
      requiredRoles.some(required => required.path === screen?.path)
    );
  }

  isLogged(): boolean {
    const jwtToken = this.getToken();
    if (jwtToken) {
      const decodedToken = this.decodeToken(jwtToken);
      return decodedToken?.exp && new Date(decodedToken.exp * 1000).getTime() > new Date().getTime();
    }
    return false;
  }

  private decodeToken(jwtToken: string): any {
    try {
      return jwtDecode(jwtToken);
    } catch (e) {
      console.error(`erro ao decodificar o token ${jwtToken}`)
      return null;
    }
  }

  logout(): void {
    localStorage.clear();
    window.stop(); // ?????
    this.currentUserSource.next(null);
    this.router.navigate(['/login']);
  }

  getToken() {
    const user = this.getCurrentUser();
    return user.accessToken;
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('user') && !!localStorage.getItem('group');
  }

  getCurrentUser(): User {
    if (!this.isAuthenticated())
      this.logout();
    if (!this.currentUserSource.value)
      this.currentUserSource.next(JSON.parse(localStorage.getItem('user') ?? '{}'));
    return structuredClone(this.currentUserSource.value);
  }

  setUserFromToken(token: any) {
    const user: User = this.decrypt(token, true);

    if (!user.contracts || user.contracts.length === 0)
      throw new Error('Usuário não possui contrato associado');

    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('group', JSON.stringify(user.contracts ? user.contracts[0] : null))

    this.currentUserSource.next(user);
  }

  decrypt(encrypted: any, shouldParse?: boolean) {
    try {
      if (encrypted) {
        const bytes: any = aes.decrypt(encrypted, AuthService.KEY);
        if (bytes) {
          if (shouldParse) {
            return JSON.parse(bytes.toString(Utf8));
          }
          return bytes.toString(Utf8);
        }
      }
    } catch (erro) {
      console.error(erro);
    }
    return null;
  }

  changePassword(data: any): Observable<any> {
    const url: string = `${this.baseUrl}auth/updatePassword`;
    return this.http.post(url, data);
  }

  tokenValido(token: string): boolean {
    if (!token)
      return false;

    return !this.jwtHelper.isTokenExpired(token);
  }

  get changeLoggedPass(): boolean {
    return this._changeLoggedPass.value;
  }

  enableChangeLoggedPass() {
    this._changeLoggedPass.next(true);
  }

  disableChangeLoggedPass() {
    this._changeLoggedPass.next(false);
  }

  get changePassSuccess(): boolean {
    return this._changePassSuccess.value;
  }

  enableChangePassSuccess() {
    this._changePassSuccess.next(true);
  }

  disableChangePassSuccess() {
    this._changePassSuccess.next(false);
  }

  setCurrentContract(value: Contract) {
    localStorage.setItem('group', JSON.stringify(value));
    this._contractSelected$.next(value);
  }

  getCurrentContract(): Contract | undefined {
    const contract = localStorage.getItem('group');
    if (contract) {
      return JSON.parse(contract);
    }
    return undefined;
  }

  getCurrentContractAsList(): Contract[] | undefined {
    const contract = localStorage.getItem('group');
    if (contract) {
      return [JSON.parse(contract)];
    }
    return undefined;
  }

  private setTenantCookie(): void {
    const d: Date = new Date();
    d.setTime(d.getTime() + (360 * 24 * 60 * 60 * 1000));
    const expires = `expires=${d.toUTCString()}`;
    document.cookie = `Tenant=f6842ee7-8e0b-4b2e-b93d-d7e542d5c34a; ${expires}`;
  }

  saveFromURL(user: any) {
    // user = this.setRoles(user);
    localStorage.setItem('user', JSON.stringify(user));
    this.setTenantCookie();
  }
}
